/**
 * TanStack Query hooks for merge approvals API.
 */

import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  fetchAllApprovals,
  approveChange,
  rejectChange,
  waiveApproval,
  type MergeApproval,
} from "../api/mergeApprovals";
import { useIsVisible } from "./useIsVisible";

export function useMergeApprovals(
  projectId: string,
  sourceBranch: string,
  targetBranch: string,
  enabled = true,
) {
  const isVisible = useIsVisible();
  const isPollingEnabled = enabled && isVisible;

  return useQuery<MergeApproval[]>({
    queryKey: ["merge-approvals", projectId, sourceBranch, targetBranch],
    queryFn: () => fetchAllApprovals(projectId, sourceBranch, targetBranch),
    enabled: isPollingEnabled && !!projectId && !!sourceBranch && !!targetBranch,
    refetchInterval: isPollingEnabled ? 30_000 : false,
  });
}

export function useApproveChange(projectId: string, sourceBranch: string, targetBranch: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (approvalId: string) => approveChange(approvalId),
    onSuccess: () =>
      qc.invalidateQueries({
        queryKey: ["merge-approvals", projectId, sourceBranch, targetBranch],
      }),
  });
}

export function useRejectChange(projectId: string, sourceBranch: string, targetBranch: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ approvalId, comment }: { approvalId: string; comment: string }) =>
      rejectChange(approvalId, comment),
    onSuccess: () =>
      qc.invalidateQueries({
        queryKey: ["merge-approvals", projectId, sourceBranch, targetBranch],
      }),
  });
}

export function useWaiveApproval(projectId: string, sourceBranch: string, targetBranch: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (approvalId: string) => waiveApproval(approvalId),
    onSuccess: () =>
      qc.invalidateQueries({
        queryKey: ["merge-approvals", projectId, sourceBranch, targetBranch],
      }),
  });
}
